import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { InfoTip } from '@/components/ui/info-tip';
import { cn } from '@/lib/utils';

const base =
  'w-full rounded-md border border-border-strong bg-panel text-fg placeholder:text-faint transition-colors ' +
  'hover:border-faint focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/40 disabled:opacity-50';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Show the terminal `>` prompt glyph. */
  prompt?: boolean;
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { prompt, invalid, className, ...props },
  ref,
) {
  const input = (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        base,
        'h-8 px-2.5 text-sm touch:h-10 touch:text-[16px]',
        prompt && 'pl-6',
        invalid && 'border-danger',
        className,
      )}
      {...props}
    />
  );
  if (!prompt) return input;
  return (
    <div className="relative w-full">
      <span
        aria-hidden
        className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 font-mono text-faint"
      >
        &gt;
      </span>
      {input}
    </div>
  );
});

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }
>(function Textarea({ className, invalid, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        base,
        'min-h-20 px-2.5 py-2 text-sm leading-relaxed touch:text-[16px]',
        invalid && 'border-danger',
        className,
      )}
      {...props}
    />
  );
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select(
  { className, children, ...props },
  ref,
) {
  return (
    <select
      ref={ref}
      className={cn(base, 'h-8 cursor-pointer px-2 text-sm touch:h-10 touch:text-[16px]', className)}
      {...props}
    >
      {children}
    </select>
  );
});

export function Label({
  htmlFor,
  children,
  className,
}: {
  htmlFor?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label htmlFor={htmlFor} className={cn('block text-xs font-medium text-muted', className)}>
      {children}
    </label>
  );
}

export function Field({
  label,
  htmlFor,
  hint,
  info,
  error,
  children,
  className,
}: {
  label: ReactNode;
  htmlFor?: string;
  hint?: ReactNode;
  /** Longer explanation, shown in a tooltip on the "i" next to the label. */
  info?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {info ? (
        <div className="flex items-center gap-1.5">
          <Label htmlFor={htmlFor}>{label}</Label>
          <InfoTip>{info}</InfoTip>
        </div>
      ) : (
        <Label htmlFor={htmlFor}>{label}</Label>
      )}
      {children}
      {error ? (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-faint">{hint}</p>
      ) : null}
    </div>
  );
}

export function Checkbox({
  checked,
  onChange,
  label,
  id,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: ReactNode;
  id?: string;
  disabled?: boolean;
}) {
  return (
    <label
      htmlFor={id}
      className={cn('inline-flex cursor-pointer items-center gap-2 text-sm', disabled && 'opacity-50')}
    >
      <input
        id={id}
        type="checkbox"
        className="peer sr-only"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span
        aria-hidden
        className={cn(
          'flex h-4 w-4 shrink-0 items-center justify-center rounded-xs border text-[10px] leading-none text-accent-fg',
          'peer-focus-visible:ring-2 peer-focus-visible:ring-accent/40',
          checked ? 'border-accent bg-accent' : 'border-border-strong bg-panel',
        )}
      >
        {checked ? '✓' : ''}
      </span>
      <span>{label}</span>
    </label>
  );
}
